import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, ListChecks, Mail, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/AppShell";

export default function Landing() {
  return (
    <AppShell>
      <section className="relative overflow-hidden">
        {/* subtle silver halo (spec: no hue-shift, just radial silver on solid black) */}
        <div
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 20% 0%, rgba(192,192,192,0.10), transparent 55%), radial-gradient(circle at 80% 15%, rgba(192,192,192,0.06), transparent 50%)",
          }}
        />
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 sm:pt-28 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-7">
            <div className="pnc-eyebrow mb-5">PNC · Absence Notifications · 2026</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#F5F5F5] leading-[1.03] tracking-tight">
              The Excel sheet is retired.<br/>
              <span className="text-[#C0C0C0]">Absence notifications, sorted.</span>
            </h1>
            <p className="mt-7 text-base lg:text-lg text-[#A8A8A8] max-w-xl leading-relaxed">
              Contractors submit absence notifications in seconds. Supervisors approve, reject or cancel with a
              reason. The main manager sees everything in one calendar &mdash; no more chasing forms or
              rewriting cells.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/request">
                <Button
                  data-testid="landing-request-btn"
                  size="lg"
                  className="gap-2 h-12 sm:h-14 px-7 rounded-xl bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A] font-medium"
                >
                  Submit an absence notification <ArrowRight className="w-4 h-4" strokeWidth={2}/>
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  data-testid="landing-login-btn"
                  size="lg"
                  variant="outline"
                  className="gap-2 h-12 sm:h-14 px-7 rounded-xl bg-[#1E1E1E] border-[#2A2A2A] text-[#F5F5F5] hover:bg-[#141414]"
                >
                  Staff sign in
                </Button>
              </Link>
            </div>

            {/* trust strip */}
            <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-[12px] uppercase tracking-[0.18em] font-semibold text-[#6B6B6B]">
              <span>UK GDPR aligned</span>
              <span className="text-[#2A2A2A]">·</span>
              <span>Encrypted at rest</span>
              <span className="text-[#2A2A2A]">·</span>
              <span>Full audit trail</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="pnc-card p-6 sm:p-8 space-y-6">
              <div className="pnc-eyebrow">What&apos;s inside</div>
              <Feature icon={<ListChecks className="w-5 h-5" strokeWidth={2}/>} title="One submission flow">
                Contractors pick site, role, supervisor and dates &mdash; that&apos;s it.
              </Feature>
              <Feature icon={<ShieldCheck className="w-5 h-5" strokeWidth={2}/>} title="Supervisor decisions, logged">
                Approve, reject or cancel &mdash; with a reason, a name and a timestamp.
              </Feature>
              <Feature icon={<CalendarCheck className="w-5 h-5" strokeWidth={2}/>} title="Calendar + filters">
                Month view, filter by department, supervisor, contractor or status.
              </Feature>
              <Feature icon={<Mail className="w-5 h-5" strokeWidth={2}/>} title="Emails out of the box">
                Resend powered. Every email previewed in the admin log.
              </Feature>
            </div>
          </div>
        </div>
      </section>
    </AppShell>
  );
}

function Feature({ icon, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-xl bg-[#141414] border border-[#2A2A2A] text-[#C0C0C0] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-medium text-[#F5F5F5]">{title}</div>
        <div className="text-sm text-[#A8A8A8] mt-0.5">{children}</div>
      </div>
    </div>
  );
}
